import React from 'react';
import {
  FunnelIcon,
  Cog6ToothIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
} from '@heroicons/react/24/outline';
import { FailureFilters } from '@/types/filters';
import { AnalysisMethod } from '@/hooks/useWeibullAnalysis';
import { VblAnalysisResult, VblForecastConstraints } from '@/types/vbl';
import { useLayout } from '@/contexts';
import { ColorByOption, MarkerByOption, SizeByOption } from './PlotCustomization';
import FilterSidebar from '../filters/FilterSidebar';
import AnalysisSettingsSidebar from './AnalysisSettingsSidebar';
import VblAnalysisSettingsSidebar from '../vbl/VblAnalysisSettingsSidebar';

interface SidebarControllerProps {
  // Filter props
  filters: FailureFilters;
  onFiltersChange: (newFilters: FailureFilters) => void;
  filterOptions: {
    sites: string[];
    turbineModels: string[];
    componentsMajor: string[];
    componentsMinor: string[];
    failureModes: string[];
    outcomeName: string[];
    excludedCaseIds: string[];
  };
  isLoading?: boolean;

  // Analysis settings props
  plotType?: 'probability' | 'cdf';
  onPlotTypeChange?: (type: 'probability' | 'cdf') => void;
  showConfidenceIntervals?: boolean;
  confidenceLevel?: number;
  onToggleConfidenceIntervals?: (enabled: boolean) => void;
  onConfidenceLevelChange?: (level: number) => void;
  showCensoredData?: boolean;
  onToggleCensoredData?: (enabled: boolean) => void;
  selectedMethod?: AnalysisMethod;
  onMethodChange?: (method: AnalysisMethod) => void;
  colorBy?: ColorByOption;
  onColorByChange?: (option: ColorByOption) => void;
  markerBy?: MarkerByOption;
  onMarkerByChange?: (option: MarkerByOption) => void;
  sizeBy?: SizeByOption;
  onSizeByChange?: (option: SizeByOption) => void;
  showLegend?: boolean;
  onShowLegendChange?: (show: boolean) => void;

  // VBL-specific props
  isVblAnalysis?: boolean;
  vblAnalysis?: VblAnalysisResult | null;
  forecastConstraints?: VblForecastConstraints | null;
  onForecastConstraintsChange?: (constraints: VblForecastConstraints) => void;
}

export const SidebarController: React.FC<SidebarControllerProps> = ({
  // Filter props
  filters,
  onFiltersChange,
  filterOptions,
  isLoading = false,

  // Analysis settings props
  plotType = 'probability',
  onPlotTypeChange,
  showConfidenceIntervals = true,
  confidenceLevel = 95,
  onToggleConfidenceIntervals,
  onConfidenceLevelChange,
  showCensoredData = true,
  onToggleCensoredData,
  selectedMethod = 'mle',
  onMethodChange,
  colorBy = 'none',
  onColorByChange,
  markerBy = 'none',
  onMarkerByChange,
  sizeBy = 'uniform',
  onSizeByChange,
  showLegend = false,
  onShowLegendChange,

  // VBL-specific props
  isVblAnalysis = false,
  vblAnalysis = null,
  forecastConstraints: _forecastConstraints = null,
  onForecastConstraintsChange: _onForecastConstraintsChange,
}) => {
  const { rightSidebarType, setRightSidebarType } = useLayout();

  // Toggle individual sidebars
  const toggleSidebar = (type: 'filters' | 'analysis') => {
    const current = rightSidebarType;
    if (current === 'none') {
      setRightSidebarType(type);
    } else if (current === type) {
      setRightSidebarType('none');
    } else if (current === 'both') {
      setRightSidebarType(type === 'filters' ? 'analysis' : 'filters');
    } else {
      setRightSidebarType('both');
    }
  };

  // Toggle all sidebars
  const toggleAll = () => {
    const current = rightSidebarType;
    if (current === 'both') {
      setRightSidebarType('none');
    } else {
      setRightSidebarType('both');
    }
  };

  // Determine if specific sidebar is active
  const isFilterSidebarActive = rightSidebarType === 'filters' || rightSidebarType === 'both';
  const isAnalysisSidebarActive = rightSidebarType === 'analysis' || rightSidebarType === 'both';

  return (
    <div className="flex h-full">
      {/* Vertically Stacked Sidebars */}
      <div className="flex flex-col h-full">
        {/* Filter Sidebar */}
        {isFilterSidebarActive && (
          <div className={`${isAnalysisSidebarActive ? 'h-1/2' : 'h-full'}`}>
            <FilterSidebar
              filters={filters}
              onFiltersChange={onFiltersChange}
              filterOptions={filterOptions}
              isLoading={isLoading}
              isCollapsed={false}
              onToggleCollapse={() => toggleSidebar('filters')}
            />
          </div>
        )}

        {/* Analysis Settings Sidebar */}
        {isAnalysisSidebarActive && (
          <div className={isFilterSidebarActive ? "h-1/2" : "h-full"}>
            {isVblAnalysis ? (
              <VblAnalysisSettingsSidebar
                plotType={plotType}
                onPlotTypeChange={onPlotTypeChange}
                showConfidenceIntervals={showConfidenceIntervals}
                confidenceLevel={confidenceLevel}
                onToggleConfidenceIntervals={onToggleConfidenceIntervals}
                onConfidenceLevelChange={onConfidenceLevelChange}
                showCensoredData={showCensoredData}
                onToggleCensoredData={onToggleCensoredData}
                colorBy={colorBy}
                onColorByChange={onColorByChange}
                markerBy={markerBy}
                onMarkerByChange={onMarkerByChange}
                sizeBy={sizeBy}
                onSizeByChange={onSizeByChange}
                showLegend={showLegend}
                onShowLegendChange={onShowLegendChange}
                vblAnalysis={vblAnalysis}
                isCollapsed={false}
                onToggleCollapse={() => toggleSidebar('analysis')}
                showCollapseBothButton={false}
                onCollapseBoth={undefined}
              />
            ) : (
              <AnalysisSettingsSidebar
                plotType={plotType}
                onPlotTypeChange={onPlotTypeChange}
                showConfidenceIntervals={showConfidenceIntervals}
                confidenceLevel={confidenceLevel}
                onToggleConfidenceIntervals={onToggleConfidenceIntervals}
                onConfidenceLevelChange={onConfidenceLevelChange}
                showCensoredData={showCensoredData}
                onToggleCensoredData={onToggleCensoredData}
                selectedMethod={selectedMethod}
                onMethodChange={onMethodChange}
                colorBy={colorBy}
                onColorByChange={onColorByChange}
                markerBy={markerBy}
                onMarkerByChange={onMarkerByChange}
                sizeBy={sizeBy}
                onSizeByChange={onSizeByChange}
                showLegend={showLegend}
                onShowLegendChange={onShowLegendChange}
                isCollapsed={false}
                onToggleCollapse={() => toggleSidebar('analysis')}
                showCollapseBothButton={false}
                onCollapseBoth={undefined}
              />
            )}
          </div>
        )}
      </div>
      
      {/* Icon Strip - Always visible on the right */}
      <div className="bg-white h-full flex flex-col w-12 relative border-l border-gray-200">
        {/* Main Toggle Button */}
        <div className="p-2">
          <button
            onClick={toggleAll}
            className="p-2 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title={rightSidebarType === 'both' ? 'Close all sidebars' : 'Open all sidebars'}
          >
            {rightSidebarType === 'both' ? (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Individual Sidebar Icons - Always visible with active state */}
        <div className="flex flex-col space-y-2 p-2">
          {/* Filter Icon */}
          <button
            onClick={() => toggleSidebar('filters')}
            className={`p-2 rounded-md transition-colors relative ${
              isFilterSidebarActive
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
            }`}
            title={isFilterSidebarActive ? 'Close filters' : 'Open filters'}
          >
            <FunnelIcon className="h-5 w-5" />
            {(() => {
              const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
                if (key === 'excludedCaseIds' && Array.isArray(value) && value.length > 0) {
                  return count + 1; // Count excluded cases as one filter
                } else if (Array.isArray(value)) {
                  return count + value.length;
                } else if (value && key !== 'page' && key !== 'size') {
                  return count + 1;
                }
                return count;
              }, 0);

              return activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              );
            })()}
          </button>

          {/* Settings Icon */}
          <button
            onClick={() => toggleSidebar('analysis')}
            className={`p-2 rounded-md transition-colors ${
              isAnalysisSidebarActive
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
            }`}
            title={isAnalysisSidebarActive ? 'Close analysis settings' : 'Open analysis settings'}
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SidebarController;
